import { useState } from 'react';

import { toast } from 'sonner';

import { DocumentDownloadOptions } from '../../../../../../../../../../../../backend/src/types/types';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDownloadKimAnswer } from '@/api/mutations/useDownloadKimAnswer';
import { useTogglePublishedThread } from '@/api/mutations/useTogglePublishedThread';
import { useGetThreadStatus } from '@/api/queries/useGetThreadStatus';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/Tooltip';
import { ThreadExportProgressModal } from '@/components/molecules/ThreadExportProgressModal';
import ThreadDownloadModal from '@/components/organisms/ThreadDownloadModal';
import { cleanReferencesAndCopyToClipboard } from '@/constants/referencing';
import { useUserContext } from '@/Contexts/User';
import { getTitleFromFlowType } from '@/helpers/kimDisplay';
import { CopyButton } from '@/new/components/copy-button';
import { DownloadButton } from '@/new/components/download-button';
import { Button } from '@/new/components/shadcn/button';
import { Artifact, ConfidenceLevelObject, KimMode } from '@/types';

interface AnswerHeaderProps {
  confidenceLevel: ConfidenceLevelObject;
  content: string;
  setShowConfidenceTooltip: (show: boolean) => void;
  caseId: string;
  threadId: string;
  messageId: string;
  artifact: Artifact | undefined;
  flowType: KimMode | undefined;
  isLoadingMessages: boolean;
}

export function AnswerHeader({
  confidenceLevel,
  content,
  setShowConfidenceTooltip,
  caseId,
  threadId,
  messageId,
  artifact,
  flowType,
  isLoadingMessages,
}: AnswerHeaderProps) {
  const [downloadStart, setDownloadStart] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showExportProgressModal, setShowExportProgressModal] = useState(false);
  const [isCreatingExport, setIsCreatingExport] = useState(false);
  const [exportError, setExportError] = useState<string | undefined>(undefined);

  const { user } = useUserContext();
  const { downloadAnswer, downloadAnswerWithDocuments } = useDownloadKimAnswer(caseId, threadId, messageId, artifact);
  const { data: threadData } = useGetThreadStatus({ caseId, threadId });
  const { mutate: toggleThreadPublished, isLoading: isToggling } = useTogglePublishedThread(caseId);

  const handleDownloadClick = () => {
    // If there's an artifact, download directly without showing modal
    if (artifact) {
      handleDownloadAnswer();
    } else {
      // Show modal for formatting options
      setShowDownloadModal(true);
    }
  };

  const handleDownloadAnswer = async (options?: DocumentDownloadOptions, includeSourceDocuments?: boolean) => {
    trackCustomEvent(TrackingEvents.DownloadKimAnswer, { caseId, threadId, includeSourceDocuments });
    setShowDownloadModal(false);

    if (includeSourceDocuments) {
      // Start async export job
      try {
        setExportError(undefined);
        const result = await downloadAnswer(options, true);

        if (result && 'exportExists' in result) {
          if (result.exportExists) {
            // Export already exists - download immediately
            await downloadAnswerWithDocuments();
          } else {
            // Job in progress or just started - show progress modal
            setIsCreatingExport(true);
            setShowExportProgressModal(true);
          }
        }
      } catch (error) {
        console.error('[AnswerHeader] Export failed:', error);
        setExportError(error instanceof Error ? error.message : 'Failed to start export');
        setShowExportProgressModal(true);
      }
    } else {
      // Original synchronous download
      setDownloadStart(true);

      try {
        await downloadAnswer(options);
      } catch (error) {
        console.error('Download failed:', error);
        toast.error('Download failed. Please try again.');
      } finally {
        // Reset state
        setDownloadStart(false);
        setDownloadComplete(true);
        setTimeout(() => setDownloadComplete(false), 1000);
      }
    }
  };

  const handleExportDownload = async () => {
    try {
      await downloadAnswerWithDocuments();
      toast.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Please try again.');
    }
  };

  const handleCloseExportModal = () => {
    setShowExportProgressModal(false);
    setIsCreatingExport(false);
    setExportError(undefined);
  };

  const copyPageLinkToClipboard = () => {
    trackCustomEvent(TrackingEvents.CopyKimPageLink, { caseId, threadId });
    navigator.clipboard.writeText(window.location.href);
    toast.info('Link to page copied to clipboard');
  };

  const handleTogglePublished = () => {
    if (!threadData?.isPublished) {
      trackCustomEvent(TrackingEvents.KimThreadPublished, { caseId, threadId });
    }
    toggleThreadPublished(threadId, {
      onSuccess: ({ isPublished }) => {
        toast.success(`Thread ${isPublished ? 'published' : 'unpublished'} successfully`);
      },
      onError: () => {
        toast.error('Failed to toggle thread published status');
      },
    });
  };

  const buttonsDisabled = isLoadingMessages || confidenceLevel.text === 'Low';

  return (
    <div className="flex h-14 items-center justify-between border-b border-gray-100 bg-gray-50/50 px-6 py-3">
      <div className="flex items-center gap-3">
        <span className="iconify lucide--lightbulb text-brandSecondary size-5" />
        <h3 className="text-sm font-medium text-gray-800">{getTitleFromFlowType(flowType)}</h3>

        <Tooltip>
          <TooltipTrigger asChild>
            <div className="ml-2 flex w-fit items-center gap-1.5 rounded-md border bg-gray-100 px-2.25 py-1 text-xs text-gray-700">
              <span className="iconify lucide--info size-4" />
              <p className="-mb-0.25">Review sources to verify {getTitleFromFlowType(flowType).toLowerCase()}</p>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-sm bg-gray-50 p-2 px-3 text-gray-700" sideOffset={8}>
            Kim has undergone extensive testing to increase the accuracy of the answers. However, as with all generative
            AI systems, in complex scenarios it could return an inaccurate answer. Crucially, you should check the
            source fact and document in the citation to verify the answer's completeness or accuracy.
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex items-center justify-between gap-2">
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                disabled={buttonsDisabled}
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={copyPageLinkToClipboard}
              >
                <span className="iconify lucide--link h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy link to this page.</p>
            </TooltipContent>
          </Tooltip>
          {flowType !== KimMode.ExtractionFlow && (
            <CopyButton
              copyHandler={() => {
                trackCustomEvent(TrackingEvents.CopyKimAnswer, { caseId, threadId });
                cleanReferencesAndCopyToClipboard(content);
                toast.success('Copied to clipboard');
              }}
              tooltipText="Copy answer to clipboard."
            />
          )}

          <DownloadButton
            handleDownload={handleDownloadClick}
            downloadStart={downloadStart}
            downloadComplete={downloadComplete}
            tooltipText="Download answer."
          />
        </>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`text-xs ${threadData?.isPublished ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800' : ''}`}
              onClick={handleTogglePublished}
              disabled={isToggling || buttonsDisabled || threadData?.createdById !== user?.id}
            >
              <span className="iconify lucide--users h-3 w-3" />
              <p className="text-xs">{threadData?.isPublished ? 'Unpublish' : 'Publish'}</p>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-56">
            <p className="text-center">
              {threadData?.createdById !== user?.id
                ? 'You can only publish threads you have created.'
                : threadData?.isPublished
                  ? 'Remove from the list of shared questions.'
                  : 'Add to the list of shared questions.'}
            </p>
          </TooltipContent>
        </Tooltip>

        <Button
          size="sm"
          onClick={() => {
            trackCustomEvent(TrackingEvents.KimConfidenceLevel, { caseId, threadId });
            setShowConfidenceTooltip(true);
          }}
          className={`relative hover:cursor-pointer ${confidenceLevel.backgroundColor} rounded-md border text-xs ${confidenceLevel.borderColor} flex items-center gap-2 hover:${confidenceLevel.backgroundColor} hover:${confidenceLevel.borderColor}`}
        >
          <p>
            <span>Confidence:</span> <b className={confidenceLevel.textColor}>{confidenceLevel.text}</b>
          </p>
          <span className="iconify lucide--info absolute top-[-8px] right-[-16px] mr-2 ml-2 cursor-pointer rounded-full text-sm text-blue-500 hover:text-blue-800" />
        </Button>
      </div>
      <ThreadDownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadAnswer}
        isLoading={downloadStart}
      />
      <ThreadExportProgressModal
        isOpen={showExportProgressModal}
        onClose={handleCloseExportModal}
        caseId={caseId}
        threadId={threadId}
        messageId={messageId}
        onDownload={handleExportDownload}
        isCreating={isCreatingExport}
        error={exportError}
      />
    </div>
  );
}
